import React, {useEffect, useState} from 'react';
import { Link } from 'react-router-dom';
import { getPreSeasonContractById, getPreseasonApplications, getFinalContractsByContractId, getPreseasonPriceHistory, getPreseasonApplicationPriceBuckets, getPreseasonQuantityHistory } from "../../api/web";
import moment from "moment";

const PreSeasonContractDetail = (props) => {

    const id = props.match.params.id;
    const contractId = parseInt(id, 10);
    const [initialLoading, setInitialLoading] = useState(true);
    const [contract, setContract] = useState(null);
    const [applications, setApplications] = useState([]);
    const [applicationsLoading, setApplicationsLoading] = useState(false);
    const [finalContracts, setFinalContracts] = useState([]);
    const [finalContractsLoading, setFinalContractsLoading] = useState(false);
    const [listPriceHistory, setListPriceHistory] = useState(null);
    const [applicationPriceBuckets, setApplicationPriceBuckets] = useState(null);
    const [priceTabLoading, setPriceTabLoading] = useState(false);
    const [quantityHistory, setQuantityHistory] = useState(null);
    const [quantityHistoryLoading, setQuantityHistoryLoading] = useState(false);

    useEffect(() => {
        getPreSeasonContractById(id).then((response) => {
            if (!response.error) {
                setContract(response.payload);
            }
            setInitialLoading(false);
        });
    }, [id]);

    useEffect(() => {
        if (!contract || !contractId) return;
        setApplicationsLoading(true);
        getPreseasonApplications().then((res) => {
            if (!res.error && Array.isArray(res.payload)) {
                const forContract = res.payload.filter(
                    (app) => (app.contract?.id ?? app.contract_id ?? app.contract) === contractId
                );
                setApplications(forContract);
            } else {
                setApplications([]);
            }
            setApplicationsLoading(false);
        });
    }, [contract, contractId]);

    useEffect(() => {
        if (!contract || !contractId) return;
        setFinalContractsLoading(true);
        getFinalContractsByContractId(contractId).then((res) => {
            if (!res.error && Array.isArray(res.payload)) {
                setFinalContracts(res.payload);
            } else {
                setFinalContracts([]);
            }
            setFinalContractsLoading(false);
        });
    }, [contract, contractId]);

    useEffect(() => {
        if (!contract || !contractId) return;
        setPriceTabLoading(true);
        Promise.all([
            getPreseasonPriceHistory(contractId),
            getPreseasonApplicationPriceBuckets(contractId),
        ])
            .then(([listRes, bucketsRes]) => {
                setListPriceHistory(listRes.error ? null : listRes.payload);
                setApplicationPriceBuckets(bucketsRes.error ? null : bucketsRes.payload);
            })
            .finally(() => {
                setPriceTabLoading(false);
            });
    }, [contract, contractId]);

    useEffect(() => {
        if (!contract || !contractId) return;
        setQuantityHistoryLoading(true);
        getPreseasonQuantityHistory(contractId).then((res) => {
            if (!res.error && res.payload) {
                setQuantityHistory(res.payload);
            } else {
                setQuantityHistory(null);
            }
            setQuantityHistoryLoading(false);
        });
    }, [contract, contractId]);

    const statusBadge = (status) => {
        const map = {
            draft: { cls: 'bg-secondary text-white', style: {} },
            open: { cls: 'bg-success text-white', style: {} },
            closed: { cls: 'bg-dark text-white', style: {} }
        };
        const c = map[status] || { cls: '', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    const applicationStatusBadge = (status) => {
        const map = {
            pending: { cls: 'text-white', style: { backgroundColor: '#fd7e14' } },
            revision: { cls: 'bg-warning text-dark', style: {} },
            approved: { cls: 'bg-success text-white', style: {} },
            finalized: { cls: 'bg-primary text-white', style: {} },
            rejected: { cls: 'bg-danger text-white', style: {} }
        };
        const c = map[status] || { cls: 'bg-secondary text-white', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    const finalContractStatusBadge = (status) => {
        const map = {
            awaiting_farmer: { cls: 'text-white', style: { backgroundColor: '#fd7e14' } },
            finalized: { cls: 'bg-success text-white', style: {} }
        };
        const c = map[status] || { cls: 'bg-secondary text-white', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    if (initialLoading) {
        return (
            <div className="content-body">
                <div className="container d-flex justify-content-center ht-100p">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mg-t-10">Loading preseason contract</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!contract) {
        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <div className="mg-b-20">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/preseason_contract">Preseason Contracts</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Not found</li>
                            </ol>
                        </nav>
                        <p className="tx-danger">Preseason contract not found.</p>
                        <Link to="/preseason_contract" className="btn btn-sm btn-primary">Back to Preseason Contracts</Link>
                    </div>
                </div>
            </div>
        );
    }

    const cropPrices = contract.crop_prices || [];

    const kgToMt = (kg) => {
        if (kg == null || Number.isNaN(Number(kg))) return '—';
        const mt = Number(kg) / 1000;
        return `${mt.toFixed(2)} MT`;
    };

    const formatPriceCell = (v) => {
        if (v == null || v === '') return '—';
        const n = Number(v);
        return Number.isFinite(n) ? n.toLocaleString() : String(v);
    };

    const listPriceHistoryEntries = Array.isArray(listPriceHistory?.entries)
        ? listPriceHistory.entries
        : [];

    const farmerDisplay = (app) => {
        if (!app.farmer) return '—';
        if (typeof app.farmer === 'object') {
            const n = [app.farmer.first_name, app.farmer.last_name].filter(Boolean).join(' ');
            return n || app.farmer.username || app.farmer.id || '—';
        }
        return `#${app.farmer}`;
    };
    const cropDisplay = (app) => {
        if (app.crop && typeof app.crop === 'object') return app.crop.name ?? app.crop.id ?? '—';
        if (app.crop != null) return `#${app.crop}`;
        return '—';
    };

    return (
        <div className="content-body">
            <div className="container pd-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/preseason_contract">Preseason Contracts</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Contract #{contract.id}</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-5 tx-spacing--1">Preseason Contract #{contract.id}</h4>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <span className="tx-color-03 tx-13">Company: {contract.company_pacra_id ?? contract.company ?? '—'}</span>
                            {statusBadge(contract.status)}
                        </div>
                    </div>
                    <div className="d-none d-md-block">
                        <Link to="/preseason_contract" className="btn btn-sm pd-x-15 btn-secondary btn-uppercase">
                            <i className="wd-10 mg-r-5 fa fa-arrow-left"></i> Back to list
                        </Link>
                    </div>
                </div>

                <div className="row row-xs">
                    <div className="col-md-12 mg-t-10">
                        <div className="nav-wrapper mg-b-20 tx-13">
                            <ul className="nav nav-line nav-fill tx-medium">
                                <li className="nav-item">
                                    <a href="#info" className="nav-link active" data-bs-toggle="tab">
                                        <i className="ion-md-information-circle"/> Information
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#crop_prices" className="nav-link" data-bs-toggle="tab">
                                        <i className="ion-md-pricetag"/> Crop Prices ({cropPrices.length})
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#price_history" className="nav-link" data-bs-toggle="tab">
                                        <i className="ion-md-time"/> Price History
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#quantity_history" className="nav-link" data-bs-toggle="tab">
                                        <i className="ion-md-stats"/> Quantity History
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#applications" className="nav-link" data-bs-toggle="tab">
                                        <i className="ion-md-document"/> Applications ({applications.length})
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#final_contracts" className="nav-link" data-bs-toggle="tab">
                                        <i className="ion-md-checkmark-done"/> Final Contracts ({finalContracts.length})
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className="tab-content">
                            <div id="info" className="tab-pane fade active show">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="row row-sm">
                                            <div className="col-md-6">
                                                <p><strong>Company (Pacra ID):</strong> {contract.company_pacra_id ?? contract.company ?? '—'}</p>
                                                <p><strong>Status:</strong> {statusBadge(contract.status)}</p>
                                                <p><strong>Total quantity (kg):</strong> {contract.total_quantity_kg != null ? Number(contract.total_quantity_kg).toLocaleString() : '—'}</p>
                                                <p><strong>Reserved quantity (kg):</strong> {contract.reserved_qty_kg != null ? Number(contract.reserved_qty_kg).toLocaleString() : '—'}</p>
                                                <p><strong>Available quantity (kg):</strong> {contract.available_qty_kg != null ? Number(contract.available_qty_kg).toLocaleString() : '—'}</p>
                                                <p><strong>Delivery location:</strong> {contract.delivery_location ?? '—'}</p>
                                                <p><strong>Delivery date:</strong> {contract.delivery_date ? moment(contract.delivery_date).format("Do MMM, YYYY") : '—'}</p>
                                                <p><strong>Application deadline:</strong> {contract.application_deadline ? moment(contract.application_deadline).format("Do MMM, YYYY") : '—'}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <p><strong>Min per farmer (kg):</strong> {contract.min_per_farmer_kg != null ? Number(contract.min_per_farmer_kg).toLocaleString() : '—'}</p>
                                                <p><strong>Max per farmer (kg):</strong> {contract.max_per_farmer_kg != null ? Number(contract.max_per_farmer_kg).toLocaleString() : '—'}</p>
                                                <p><strong>Province:</strong> {contract.province ?? (typeof contract.province === 'object' ? contract.province?.name : '—')}</p>
                                                <p><strong>Town:</strong> {contract.town ?? (typeof contract.town === 'object' ? contract.town?.name : '—')}</p>
                                                <p><strong>Date created:</strong> {contract.date_created ? moment(contract.date_created).format("Do MMM, YYYY HH:mm") : '—'}</p>
                                                {contract.terms ? (
                                                    <p><strong>Terms:</strong><br/><span className="tx-color-03">{contract.terms}</span></p>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div id="crop_prices" className="tab-pane fade">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Crop Prices</h6>
                                    </div>
                                    <div className="card-body">
                                        {cropPrices.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No crop prices defined.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Crop</th>
                                                            <th>Price per kg</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cropPrices.map((item, idx) => (
                                                            <tr key={item.id || idx}>
                                                                <td>{item.crop_name ?? item.crop ?? '—'}</td>
                                                                <td>{item.price_per_kg != null ? Number(item.price_per_kg).toLocaleString() : '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div id="price_history" className="tab-pane fade">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">List price history</h6>
                                        <p className="tx-12 tx-color-03 mg-b-0 mg-t-5">Advertised crop prices on this contract (create and edits).</p>
                                    </div>
                                    <div className="card-body">
                                        {priceTabLoading ? (
                                            <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading price data...</span>
                                            </div>
                                        ) : listPriceHistoryEntries.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No list price changes recorded yet.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>When</th>
                                                            <th>Crop</th>
                                                            <th>Previous price / kg</th>
                                                            <th>New price / kg</th>
                                                            <th>Recorded by</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {listPriceHistoryEntries.map((entry) => (
                                                            <tr key={entry.id}>
                                                                <td>{entry.created_at ? moment(entry.created_at).format('Do MMM, YYYY HH:mm') : '—'}</td>
                                                                <td>{entry.crop_name ?? '—'}</td>
                                                                <td>{formatPriceCell(entry.previous_price_per_kg)}</td>
                                                                <td>{formatPriceCell(entry.new_price_per_kg)}</td>
                                                                <td>{entry.created_by_username ?? '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="card mg-t-20">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Application volumes by price</h6>
                                        <p className="tx-12 tx-color-03 mg-b-0 mg-t-5">Reserved (approved) vs finalized volumes grouped by locked price.</p>
                                    </div>
                                    <div className="card-body">
                                        {priceTabLoading ? (
                                            <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading...</span>
                                            </div>
                                        ) : (
                                            <>
                                                {cropPrices.length === 0 ? (
                                                    <p className="tx-color-03 mg-b-0">No crop prices configured.</p>
                                                ) : applicationPriceBuckets?.history ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Crop</th>
                                                                    <th>Reserved (approved)</th>
                                                                    <th>Finalized (signed)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cropPrices.map((item, idx) => {
                                                                    const cropId = item.crop ?? item.crop_id ?? item.cropId;
                                                                    const key = cropId != null ? String(cropId) : null;
                                                                    const entry = key && applicationPriceBuckets.history ? (applicationPriceBuckets.history[key] ?? null) : null;
                                                                    const reserved = entry?.reserved ?? [];
                                                                    const finalized = entry?.finalized ?? [];

                                                                    const renderBuckets = (buckets) => {
                                                                        if (!buckets || buckets.length === 0) return '—';
                                                                        return buckets.map((b, i) => {
                                                                            const price = b.price_per_kg != null ? Number(b.price_per_kg).toLocaleString() : '—';
                                                                            const qtyKg = b.quantity_kg;
                                                                            return (
                                                                                <div key={i} className="mg-b-5">
                                                                                    {price}/kg • {kgToMt(qtyKg)}
                                                                                </div>
                                                                            );
                                                                        });
                                                                    };

                                                                    return (
                                                                        <tr key={item.id || idx}>
                                                                            <td>{item.crop_name ?? item.crop ?? '—'}</td>
                                                                            <td>{renderBuckets(reserved)}</td>
                                                                            <td>{renderBuckets(finalized)}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className="tx-color-03 mg-b-0">No application price bucket data yet.</p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div id="quantity_history" className="tab-pane fade">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Quantity History</h6>
                                    </div>
                                    <div className="card-body">
                                        {quantityHistoryLoading ? (
                                            <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading quantity history...</span>
                                            </div>
                                        ) : (
                                            <>
                                                {quantityHistory?.entries && quantityHistory.entries.length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>When</th>
                                                                    <th>Previous (kg)</th>
                                                                    <th>New total (kg)</th>
                                                                    <th>By</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {quantityHistory.entries.map((row) => (
                                                                    <tr key={row.id}>
                                                                        <td>{row.created_at ? moment(row.created_at).format('YYYY-MM-DD HH:mm') : '—'}</td>
                                                                        <td>{row.previous_total_quantity_kg == null ? '—' : row.previous_total_quantity_kg}</td>
                                                                        <td>{row.new_total_quantity_kg ?? '—'}</td>
                                                                        <td>{row.created_by_username ?? '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className="tx-color-03 mg-b-0">No quantity history yet.</p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div id="applications" className="tab-pane fade">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Preseason Applications</h6>
                                    </div>
                                    <div className="card-body">
                                        {applicationsLoading ? (
                                            <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading applications...</span>
                                            </div>
                                        ) : applications.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No applications for this contract.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Farmer</th>
                                                            <th>Crop</th>
                                                            <th>Requested (kg)</th>
                                                            <th>Approved (kg)</th>
                                                            <th>Status</th>
                                                            <th>Preferred delivery</th>
                                                            <th>Created</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {applications.map((app) => (
                                                            <tr key={app.id}>
                                                                <td>{farmerDisplay(app)}</td>
                                                                <td>{cropDisplay(app)}</td>
                                                                <td>{app.requested_qty_kg != null ? Number(app.requested_qty_kg).toLocaleString() : '—'}</td>
                                                                <td>{app.approved_qty_kg != null ? Number(app.approved_qty_kg).toLocaleString() : '—'}</td>
                                                                <td>{applicationStatusBadge(app.status)}</td>
                                                                <td>{app.preferred_delivery_date ? moment(app.preferred_delivery_date).format("Do MMM, YYYY") : '—'}</td>
                                                                <td>{app.created_at ? moment(app.created_at).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div id="final_contracts" className="tab-pane fade">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Final Contracts</h6>
                                    </div>
                                    <div className="card-body">
                                        {finalContractsLoading ? (
                                            <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading final contracts...</span>
                                            </div>
                                        ) : finalContracts.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No final contracts for this preseason contract.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Application</th>
                                                            <th>Farmer</th>
                                                            <th>Crop</th>
                                                            <th>Approved (kg)</th>
                                                            <th>Status</th>
                                                            <th>Company signed</th>
                                                            <th>Farmer confirmed</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {finalContracts.map((fc) => (
                                                            <tr key={fc.id}>
                                                                <td>#{fc.application?.id ?? '—'}</td>
                                                                <td>{fc.application ? farmerDisplay(fc.application) : '—'}</td>
                                                                <td>{fc.application ? cropDisplay(fc.application) : '—'}</td>
                                                                <td>{fc.application?.approved_qty_kg != null ? Number(fc.application.approved_qty_kg).toLocaleString() : '—'}</td>
                                                                <td>{finalContractStatusBadge(fc.status)}</td>
                                                                <td>{fc.company_signed_at ? moment(fc.company_signed_at).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                                <td>{fc.farmer_confirmed_at ? moment(fc.farmer_confirmed_at).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreSeasonContractDetail;
